import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { requestFitView } from '../../../store/workflow/workflowSlice';
import FlowPreview from '../wizard/FlowPreview';
import { checkDfd } from '../utils/dfdRules';
import { normalizeProposal, withFreshIds } from './proposalCanvas';
import { useTranslation } from 'react-i18next';

interface Props {
  artifact: any;
  rationale?: string;
}

/**
 * 어시스턴트 제안 카드 — 그림으로 먼저 보여 주고, 적용은 사용자가 누른다.
 * 적용은 언두 한 단계로 기록되므로(replaceCanvas·addFragment) 마음에 안 들면 Ctrl+Z 로 되돌린다.
 */
const ProposalCard: React.FC<Props> = ({ artifact, rationale }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasWorkflow = useSelector((s: RootState) => !!s.workflow.current?.id);
  const canvasEmpty = useSelector((s: RootState) => s.workflow.nodes.length === 0);
  const [applied, setApplied] = useState<'' | 'replaced' | 'added'>('');
  const [showRationale, setShowRationale] = useState(false);

  const { fragment, dropped } = useMemo(() => normalizeProposal(artifact), [artifact]);
  const violations = useMemo(
    () => checkDfd(fragment.nodes as any, fragment.edges as any),
    [fragment],
  );

  if (!fragment.nodes.length) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[0.6875rem] text-amber-800">
        {t('assistant:proposal.noNodes')}
        {dropped.length > 0 && <div className="mt-1">{dropped.join(' · ')}</div>}
      </div>
    );
  }

  const apply = (mode: 'replace' | 'add') => {
    if (!hasWorkflow) return;
    if (mode === 'replace' && !canvasEmpty) {
      // 되돌릴 수 있다는 사실을 함께 알려 준다 — 지우는 동작이라 먼저 묻는다
      if (!window.confirm(t('assistant:proposal.replaceConfirm'))) return;
    }
    if (mode === 'replace') {
      dispatch({ type: 'workflow/replaceCanvas', payload: fragment });
    } else {
      dispatch({ type: 'workflow/addFragment', payload: withFreshIds(fragment) });
    }
    dispatch(requestFitView());
    setApplied(mode === 'replace' ? 'replaced' : 'added');
  };

  return (
    <div className="rounded border border-emerald-300 bg-emerald-50/60 p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[0.6875rem] font-semibold text-emerald-800">{t('assistant:proposal.title')}</span>
        <span className="text-[0.625rem] text-emerald-700">
          {t('assistant:proposal.counts', { nodes: fragment.nodes.length, edges: fragment.edges.length })}
        </span>
      </div>

      <div className="rounded border border-emerald-200 bg-white">
        <FlowPreview nodes={fragment.nodes} edges={fragment.edges} height={150} />
      </div>

      {violations.length > 0 && (
        <div className="mt-1 rounded bg-amber-50 px-2 py-1 text-[0.625rem] text-amber-800">
          {t('assistant:proposal.dfd', { n: violations.length, first: violations[0].message })}
          {violations.length > 1 && t('assistant:proposal.more', { n: violations.length - 1 })}
        </div>
      )}
      {dropped.length > 0 && (
        <div className="mt-1 rounded bg-gray-100 px-2 py-1 text-[0.625rem] text-gray-600">
          {t('assistant:proposal.dropped', { n: dropped.length, first: dropped[0] })}
        </div>
      )}

      {rationale && (
        <div className="mt-1">
          <button
            type="button"
            className="text-[0.625rem] text-emerald-700 underline"
            onClick={() => setShowRationale((v) => !v)}
          >
            {showRationale ? t('assistant:proposal.hideWhy') : t('assistant:proposal.showWhy')}
          </button>
          {showRationale && (
            <div className="mt-1 whitespace-pre-wrap rounded bg-white/70 p-2 text-[0.6875rem] leading-relaxed text-gray-700">
              {rationale}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center gap-1">
        <button
          type="button"
          disabled={!hasWorkflow}
          onClick={() => apply('replace')}
          className="rounded bg-emerald-600 px-2 py-1 text-[0.6875rem] font-medium text-white hover:bg-emerald-700 disabled:bg-gray-300"
        >
          {t('assistant:proposal.apply')}
        </button>
        <button
          type="button"
          disabled={!hasWorkflow}
          onClick={() => apply('add')}
          className="rounded border border-emerald-600 px-2 py-1 text-[0.6875rem] font-medium text-emerald-700 hover:bg-emerald-100 disabled:border-gray-300 disabled:text-gray-400"
        >
          {t('assistant:proposal.append')}
        </button>
        {applied && (
          <span className="text-[0.625rem] text-gray-500">
            {applied === 'replaced' ? t('assistant:proposal.applied') : t('assistant:proposal.appended')}{t('assistant:proposal.undo')}
          </span>
        )}
        {!hasWorkflow && <span className="text-[0.625rem] text-gray-500">{t('assistant:proposal.openWorkflow')}</span>}
      </div>
    </div>
  );
};

export default ProposalCard;
